<script setup>
import queryString from "query-string";
import { watchDebounced } from "@vueuse/core";

definePageMeta({
  auth: false,
});

const tabs = [
  { label: "All", value: "all" },
  { label: "Highest ranking", value: "ranking" },
  // { label: "Public university", value: "public" },
  { label: "Private university", value: "private" },
];

const activeTab = ref("all");
const pageNumber = ref(1);
const pageSize = ref(10);

const search = ref("");
const debounceSearch = ref(search.value);

const showSearch = ref(false);

watchDebounced(
  search,
  (newValue) => {
    pageNumber.value = 1;
    debounceSearch.value = newValue;
  },
  { debounce: 500, maxWait: 6000 },
);

const queryStr = computed(() => {
  const qStr = queryString.stringify(
    {
      universityType: activeTab.value,
      page: pageNumber.value,
      pageSize: pageSize.value,
      rank: activeTab.value === "ranking" ? "highest" : null,
      name: debounceSearch.value || null,
    },
    { arrayFormat: "index" },
  );

  return qStr;
});

const { data: universities, status } = await usePublicFetch(
  () => `universities?published=${true}&${queryStr.value}`,
  {
    key: "all-universities",
    transform(universities) {
      const modifyUniversities = universities?.data?.map((uni) => ({
        name: uni?.name,
        id: uni?.id,
        slug: uni?.slug,
        type: uni?.type,
        programCount: uni?.program_count,
        ranking: getRankingLabel(uni?.ranking),
        rankingNumber: uni?.ranking?.number,
        city: getCitiesName(uni?.cities),
        cities: uni?.cities,
        profileUrl: uni?.media?.at(0)?.original_url,
        totalStudents: uni?.attributes?.data?.find(
          (att) => att.key === "student_count",
        )?.value,
        logoUrl: getUniversityLogo(uni?.media),
      }));

      return { modifyUniversities, totalUniversity: universities?.total };
    },
    cache: "force-cache",
    staleTime: 60 * 60 * 12,
    dedupingInterval: 60 * 60 * 12,
  },
);

function updateActiveTab(tab) {
  pageNumber.value = 1;
  activeTab.value = tab;
}

const loadData = (event) => {
  const { page } = event;
  pageNumber.value = page + 1;
};

// --- SEO ---
useHead({
  link: [{ rel: "canonical", href: "https://unilinks.co/universities" }],
});

useSeoMeta({
  title: "Top Thai Universities & Rankings | Study in Thailand with UniLinks",
  ogTitle: "Top Thai Universities & Rankings | Study in Thailand with UniLinks",
  description:
    "Discover top universities in Thailand including Bangkok University and Chiang Mai University. Learn about rankings, programs, and how to choose the right university.",
  ogDescription:
    "Discover top universities in Thailand including Bangkok University and Chiang Mai University. Learn about rankings, programs, and how to choose the right university.",
  ogImage: META_IMAGE_URL,
  ogUrl: "https://unilinks.co/universities",
  twitterCard: "summary_large_image",
  twitterTitle:
    "Top Thai Universities & Rankings | Study in Thailand with UniLinks",
  twitterDescription:
    "Discover top universities in Thailand including Bangkok University and Chiang Mai University. Learn about rankings, programs, and how to choose the right university.",
  twitterImage: META_IMAGE_URL,
});
</script>

<template>
  <NuxtLayout name="primary">
    <div class="hidden lg:block bg-gray-50 mx-4">
      <div class="mx-auto w-full max-w-7xl px-2 lg:px-0 mb-4">
        <div class="py-5 pt-12 flex items-center justify-between">
          <h1 class="text-3xl font-bold text-primary-500">
            {{ universities?.totalUniversity }} Universities in Thailand
          </h1>
          <InputText
            v-model="search"
            placeholder="Search by university"
            class="w-64"
          />
        </div>

        <div class="flex flex-nowrap w-full">
          <div
            v-for="tab in tabs"
            :key="tab.value + 'program-list-key'"
            :class="[
              'p-4 border-b-2 cursor-pointer',
              activeTab === tab.value ? 'border-b-blue-500 text-blue-500' : '',
            ]"
            @click="() => updateActiveTab(tab.value)"
          >
            {{ tab.label }}
          </div>

          <div class="flex-1 border-b-2"></div>
        </div>

        <div class="grid grid-cols-12 gap-8 py-8">
          <div
            v-if="status === 'pending'"
            class="col-span-6 rounded-lg border border-slate-200 bg-white p-6 flex flex-nowrap items-start gap-8"
            v-for="i in 4"
            :key="i + 'univ-card-key-sf'"
          >
            <Skeleton size="6rem"></Skeleton>

            <div class="space-y-6 flex-1">
              <div class="flex flex-nowrap items-start gap-4">
                <div class="space-y-2 flex-1">
                  <Skeleton height="2rem"></Skeleton>
                  <Skeleton width="20rem"></Skeleton>
                </div>

                <Skeleton height="2rem"></Skeleton>
              </div>

              <div class="grid grid-cols-12 gap-x-10 gap-y-6">
                <div
                  v-for="i in 4"
                  :key="'uni-cate-card-key' + i"
                  class="col-span-6 w-full flex gap-4 items-start flex-nowrap place-self-start"
                >
                  <Skeleton size="2rem"></Skeleton>

                  <div class="space-y-1.5">
                    <Skeleton width="10rem" height="2rem"></Skeleton>
                    <Skeleton width="10rem"></Skeleton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="col-span-6 rounded-lg border border-slate-200 bg-white p-6 flex flex-nowrap items-start gap-8"
            v-for="university in universities?.modifyUniversities"
            :key="university?.id + 'university-key'"
          >
            <Avatar
              v-if="university?.logoUrl"
              :image="university?.logoUrl"
              class="w-[110px] h-[86px]"
              size="xlarge"
            />
            <Avatar
              v-else
              :label="university?.name.slice(0, 1)"
              class="w-[110px] h-[86px]"
              size="xlarge"
            />
            <!-- <img :src="university?.profileUrl" class="w-[110px] h-[98px]" alt="university image"></img> -->

            <div class="space-y-6 flex-1">
              <div class="flex flex-nowrap items-start gap-4">
                <div class="space-y-2 flex-1">
                  <NuxtLink :to="`/universities/${university?.slug}`"
                    ><p
                      class="text-xl cursor-pointer font-semibold underline underline-offset-4 text-primary-600"
                    >
                      {{ university?.name }}
                    </p></NuxtLink
                  >

                  <p class="text-base text-gray-600">
                    {{ university?.type }}
                  </p>
                </div>

                <WishlistBtn :id="university?.id" />
              </div>

              <div class="grid grid-cols-12 gap-x-10 gap-y-6">
                <div
                  class="col-span-6 w-full flex gap-4 items-start flex-nowrap place-self-start"
                >
                  <i
                    class="text-md pi pi-building text-xl text-[#4B5563] mt-1"
                  ></i>

                  <div class="space-y-1.5">
                    <p class="text-sm font-normal text-[#4B5563]">
                      {{ "Asia Ranking " + new Date().getFullYear() }}
                    </p>
                    <p
                      :class="{
                        'text-lg font-bold text-primary-500':
                          university?.rankingNumber > 0,
                        'text-base text-gray-800':
                          university?.rankingNumber === 0,
                      }"
                    >
                      {{ university?.ranking }}
                    </p>
                  </div>
                </div>

                <div
                  class="col-span-6 w-full flex gap-4 items-start flex-nowrap place-self-start"
                >
                  <i
                    class="text-md pi pi-map-marker text-xl text-[#4B5563] mt-1"
                  ></i>

                  <div class="space-y-1.5">
                    <p class="text-sm font-normal text-[#4B5563]">Location</p>
                    <p
                      :class="{
                        'text-lg font-bold text-primary-500':
                          university?.cities?.length > 0,
                        'text-base text-gray-600':
                          university?.cities?.length === 0,
                      }"
                    >
                      {{
                        university?.cities?.length > 0
                          ? getCitiesName(university?.cities)
                          : "Not provided yet"
                      }}
                    </p>
                  </div>
                </div>

                <div
                  class="col-span-6 w-full flex gap-4 items-start flex-nowrap place-self-start"
                >
                  <i
                    class="text-md pi pi-graduation-cap text-xl text-[#4B5563] mt-1"
                  ></i>

                  <div class="space-y-1.5">
                    <p class="text-sm font-normal text-[#4B5563]">
                      Number of campus
                    </p>
                    <p
                      v-if="university?.cities?.length > 0"
                      class="text-lg font-bold text-primary-500"
                    >
                      {{
                        university?.cities?.length === 1
                          ? "Single Campus"
                          : numberFormatter.format(university?.cities?.length)
                      }}
                    </p>
                    <p v-else class="text-base text-gray-600">
                      Data Not Available
                    </p>
                  </div>
                </div>

                <div
                  class="col-span-6 w-full flex gap-4 items-start flex-nowrap place-self-start"
                >
                  <i class="text-md pi pi-book text-xl text-[#4B5563] mt-1"></i>

                  <div class="space-y-1.5">
                    <p class="text-sm font-normal text-[#4B5563]">Programs</p>
                    <p
                      v-if="university?.programCount > 0"
                      class="text-lg font-bold text-primary-500"
                    >
                      {{ numberFormatter.format(university?.programCount) }}
                    </p>
                    <p v-else class="text-base text-gray-800">
                      Data Not Available
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paginator
        :rows="pageSize"
        :totalRecords="universities?.totalUniversity"
        @page="loadData"
        class="mt-4 mb-10"
      ></Paginator>
    </div>

    <!-- mobile -->
    <div class="block lg:hidden bg-gray-50 mx-4">
      <div class="mx-auto w-full max-w-[1260px] lg:px-2">
        <div class="my-5 flex items-center justify-between">
          <h1 class="text-xl font-bold text-primary-500">
            {{ universities?.totalUniversity }} Universities
          </h1>
          <Button
            v-if="!showSearch"
            icon="pi pi-search"
            text
            rounded
            aria-label="Filter"
            @click="showSearch = true"
          />
          <Button
            v-else
            icon="pi pi-times"
            text
            rounded
            aria-label="Filter"
            @click="showSearch = false"
          />
        </div>

        <InputText
          v-if="showSearch"
          v-model="search"
          placeholder="Search by university"
          class="w-full my-4"
        />

        <div class="grid grid-cols-3 lg:flex lg:flex-nowrap w-full">
          <div
            v-for="tab in tabs"
            :key="tab.value + 'program-list-key'"
            :class="[
              'p-2 lg:p-4 border-b-2 cursor-pointer text-sm lg:text-base text-center',
              activeTab === tab.value ? 'border-b-blue-500 text-blue-500' : '',
            ]"
            @click="() => updateActiveTab(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>

        <div
          class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 py-6 lg:py-8"
        >
          <div
            v-for="university in universities?.modifyUniversities"
            :key="university?.id + 'university-key'"
            class="rounded-lg border border-slate-200 bg-white p-6 lg:p-6 space-y-6"
          >
            <!-- Top Row: Image - Name - Wishlist -->
            <div class="flex items-start justify-between gap-2 md:gap-4">
              <div class="flex items-start gap-2 md:gap-4">
                <Avatar
                  v-if="university?.logoUrl"
                  :image="university?.logoUrl"
                  class="!shrink-0 w-[70px] h-[70px] lg:w-[110px] lg:h-[86px]"
                  size="xlarge"
                />
                <Avatar
                  v-else
                  :label="university?.name.slice(0, 1)"
                  class="!shrink-0 w-[70px] h-[70px] lg:w-[110px] lg:h-[86px]"
                  size="xlarge"
                />

                <div class="space-y-1">
                  <NuxtLink :to="`/universities/${university?.slug}`">
                    <p
                      class="text-base lg:text-xl font-semibold underline underline-offset-4 text-primary-600"
                    >
                      {{ university?.name }}
                    </p>
                  </NuxtLink>
                  <p class="text-sm lg:text-[17px] font-normal text-gray-600">
                    {{ university?.type }}
                  </p>
                </div>
              </div>

              <WishlistBtn :id="university?.id" />
            </div>

            <!-- Info Section: 2x2 Grid -->
            <div class="grid grid-cols-2 gap-x-4 gap-y-4">
              <div class="flex gap-2 items-start">
                <i class="pi pi-building text-xl text-[#4B5563] mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-xs text-[#4B5563]">
                    {{ "Asia Ranking " + new Date().getFullYear() }}
                  </p>
                  <p
                    v-if="university?.ranking == 'EduRank Ranking 0'"
                    class="italic text-muted"
                  >
                    Unavailable
                  </p>
                  <p
                    v-else
                    class="text-sm md:text-base font-bold text-primary-500"
                  >
                    {{ university?.ranking }}
                  </p>
                </div>
              </div>

              <div class="flex gap-2 items-start">
                <i class="pi pi-map-marker text-xl text-[#4B5563] mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-xs text-[#4B5563]">Location</p>
                  <p class="text-sm md:text-base font-bold text-primary-500">
                    {{
                      university?.cities?.length > 0
                        ? getCitiesName(university?.cities)
                        : "Not provided yet"
                    }}
                  </p>
                </div>
              </div>

              <div class="flex gap-2 items-start">
                <i class="pi pi-graduation-cap text-xl text-[#4B5563] mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-xs text-[#4B5563]">Number of campus</p>
                  <p class="text-sm md:text-base font-bold text-primary-500">
                    {{ numberFormatter.format(university?.cities?.length) }}
                  </p>
                </div>
              </div>

              <div class="flex gap-2 items-start">
                <i class="pi pi-book text-xl text-[#4B5563] mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-xs text-[#4B5563]">Programs</p>

                  <p
                    v-if="university?.programCount"
                    class="text-sm md:text-base font-bold text-primary-500"
                  >
                    {{ numberFormatter.format(university?.programCount) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paginator
        class="w-full"
        template="PrevPageLink CurrentPageReport NextPageLink"
        :rows="pageSize"
        :totalRecords="universities?.totalUniversity"
        @page="loadData"
        currentPageReportTemplate="Page {currentPage} of {totalPages}"
        :pt="{
          root: 'px-4 w-full pb-4',
          content: 'flex justify-between items-center',
          prev: 'px-2.5 py-1.5 rounded-md border border-gray-300',
          next: 'px-2.5 py-1.5 rounded-md border border-gray-300',
        }"
      >
        <template #nexticon>
          <i class="pi pi-arrow-right" />
        </template>
        <template #previcon>
          <i class="pi pi-arrow-left" />
        </template>
      </Paginator>
    </div>
    <Footer />
  </NuxtLayout>
</template>
